import { Modal, Typography, theme as antdTheme } from 'antd';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Shortcut {
  keys: string[];
  description: string;
}

interface Group {
  group: string;
  items: Shortcut[];
}

/**
 * Static list of registered shortcuts. Keep in sync with <AppShortcuts>.
 * (Reading from the runtime registry would let stale shortcuts leak into
 * the cheatsheet if a component unmounted while the modal was open, so the
 * source of truth here is intentional.)
 */
const GROUPS: Group[] = [
  {
    group: 'General',
    items: [
      { keys: ['?'], description: 'Show this cheatsheet' },
      { keys: ['⌘', 'K'], description: 'Open command palette' },
      { keys: ['b'], description: 'Back' },
      { keys: ['r'], description: 'Refresh current page data' },
      { keys: ['n'], description: 'New record (context-aware)' },
      { keys: ['Esc'], description: 'Close palette / dialog' },
    ],
  },
  {
    group: 'Jump to',
    items: [
      { keys: ['g', 'd'], description: 'Dashboard' },
      { keys: ['g', 's'], description: 'Students' },
      { keys: ['g', 'f'], description: 'Fees' },
      { keys: ['g', 'a'], description: 'Attendance' },
      { keys: ['g', 'h'], description: 'HR' },
      { keys: ['g', 'x'], description: 'Finance' },
      { keys: ['g', 'r'], description: 'Reports' },
      { keys: ['g', 'o'], description: 'Front desk' },
      { keys: ['g', 'i'], description: 'Inventory' },
      { keys: ['g', 'c'], description: 'Academics' },
    ],
  },
];

export function Cheatsheet({ open, onClose }: Props) {
  const { token } = antdTheme.useToken();
  return (
    <Modal
      title="Keyboard shortcuts"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={520}
    >
      <div style={{ display: 'grid', gap: 20 }}>
        {GROUPS.map((g) => (
          <section key={g.group}>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}
            >
              {g.group}
            </Typography.Text>
            <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
              {g.items.map((s) => (
                <div
                  key={s.keys.join('+') + s.description}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <span style={{ fontSize: 13 }}>{s.description}</span>
                  <span style={{ display: 'inline-flex', gap: 4 }}>
                    {s.keys.map((k, i) => (
                      <kbd key={i} className="palette-kbd" style={{ fontSize: 12 }}>
                        {k}
                      </kbd>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </Modal>
  );
}
